import { supabase } from '@/lib/supabase';
import type { Database } from '@/lib/database.types';

type AuctionRow = Database['public']['Tables']['auctions']['Row'];
type BidRow = Database['public']['Tables']['bids']['Row'];
type InquiryRow = Database['public']['Tables']['inquiries']['Row'];
type AttachmentRow = Database['public']['Tables']['auction_attachments']['Row'];

export interface BidResponse {
  success: boolean;
  error?: string;
  bid?: BidRow;
}

export interface InquiryResponse {
  success: boolean;
  error?: string;
}

const FUNCTION_BASE = import.meta.env.VITE_SUPABASE_FUNCTIONS_URL || '/.netlify/functions';

// ============================================
// AUCTIONS
// ============================================

export async function fetchAuctionByAktenzeichen(aktenzeichen: string): Promise<AuctionRow | null> {
  const { data, error } = await supabase.functions.invoke('get-auction', {
    query: { aktenzeichen }
  });
  
  if (error || !data?.auction) {
    console.error('fetchAuctionByAktenzeichen error:', error);
    return null;
  }
  return data.auction as AuctionRow;
}

export async function fetchAuctions(): Promise<AuctionRow[]> {
  const { data, error } = await supabase.functions.invoke('list-auctions');
  
  if (error || !data?.auctions) {
    console.error('fetchAuctions error:', error);
    return [];
  }
  return data.auctions as AuctionRow[];
}

export async function fetchAttachments(auctionId: string): Promise<AttachmentRow[]> {
  const { data, error } = await supabase.functions.invoke('list-attachments', {
    query: { auctionId }
  });
  
  if (error || !data?.attachments) {
    console.error('fetchAttachments error:', error);
    return [];
  }
  return data.attachments as AttachmentRow[];
}

export async function getAttachmentSignedUrl(storagePath: string): Promise<string | null> {
  const { data, error } = await supabase.storage
    .from('auction-attachments')
    .createSignedUrl(storagePath, 300); // 5 minutes

  if (error) return null;
  return data.signedUrl;
}

// ============================================
// BIDS
// ============================================

export async function fetchBidsForAuction(auctionId: string): Promise<BidRow[]> {
  const { data, error } = await supabase
    .from('bids')
    .select('*')
    .eq('auction_id', auctionId)
    .order('betrag', { ascending: false })
    .limit(100);

  if (error) throw error;
  return data;
}

export async function fetchMyBids(auctionId: string): Promise<BidRow[]> {
  const { data, error } = await supabase
    .from('bids')
    .select('*')
    .eq('auction_id', auctionId)
    .order('abgegeben_at', { ascending: false });

  if (error) throw error;
  return data;
}

export async function placeBid(auctionId: string, betrag: number): Promise<BidResponse> {
  try {
    const response = await supabase.functions.invoke('place-bid', {
      body: { auction_id: auctionId, betrag },
    });

    if (response.error) {
      return { success: false, error: response.error.message || 'Gebotsabgabe fehlgeschlagen' };
    }

    return { success: true, bid: response.data };
  } catch (err) {
    return { success: false, error: err instanceof Error ? err.message : 'Unbekannter Fehler' };
  }
}

// ============================================
// INQUIRIES
// ============================================

export async function submitInquiry(
  auctionId: string,
  frage: string,
): Promise<InquiryResponse> {
  try {
    const response = await supabase.functions.invoke('submit-inquiry', {
      body: { auction_id: auctionId, frage },
    });

    if (response.error) {
      return { success: false, error: response.error.message || 'Anfrage fehlgeschlagen' };
    }

    return { success: true };
  } catch (err) {
    return { success: false, error: err instanceof Error ? err.message : 'Unbekannter Fehler' };
  }
}

export async function fetchMyInquiries(): Promise<InquiryRow[]> {
  const { data, error } = await supabase
    .from('inquiries')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data;
}

// ============================================
// ANALYTICS TRACKING
// ============================================

export async function trackPageView(auctionId: string, referrer?: string) {
  try {
    const response = await supabase.functions.invoke('track-view', {
      body: {
        action: 'page_view',
        auction_id: auctionId,
        session_id: sessionStorage.getItem('analytics_session_id') || crypto.randomUUID(),
        referrer,
      },
    });

    if (response.error) {
      console.error('track-view error:', response.error);
      return null;
    }

    return response.data?.page_view_id || null;
  } catch (err) {
    console.error('track-view failed:', err);
    return null;
  }
}

export async function trackSectionFocus(
  pageViewId: string,
  sections: Array<{ section_id: string; focus_seconds: number }>,
) {
  if (!pageViewId || sections.length === 0) return;

  try {
    const response = await supabase.functions.invoke('track-view', {
      body: {
        action: 'section_focus_batch',
        page_view_id: pageViewId,
        sections,
      },
    });

    if (response.error) {
      console.error('section_focus error:', response.error);
    }
  } catch (err) {
    // Don't throw — tracking failure should not block the user
  }
}

// ============================================
// ADMIN SERVICES
// ============================================

export async function fetchAdminAuctions() {
  const { data, error } = await supabase
    .from('auctions')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data;
}

export async function fetchAdminStats() {
  const { data: auctions } = await supabase
    .from('auctions')
    .select('id, status')
    .in('status', ['LAUFEND', 'LAUFEND_RESERVE'] as string[]);

  const { data: users } = await supabase
    .from('profiles')
    .select('id, status, created_at');

  const { data: inquiries } = await supabase
    .from('inquiries')
    .select('id, created_at, status, beantwortet_at')
    .gte('created_at', new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString());

  return {
    activeAuctions: auctions?.length || 0,
    totalUsers: users?.length || 0,
    newUsers7d: users?.filter((u) => new Date(u.created_at) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)).length || 0,
    newInquiries24h: inquiries?.length || 0,
    avgResponseTimeMs: calculateAvgResponseTime(inquiries || []),
  };
}

function calculateAvgResponseTime(inquiries: any[]): number {
  const answered = inquiries.filter((i) => i.beantwortet_at);
  if (answered.length === 0) return 0;

  const total = answered.reduce((sum, i) => {
    return sum + (new Date(i.beantwortet_at).getTime() - new Date(i.created_at).getTime());
  }, 0);

  return total / answered.length;
}

// ============================================
// PROFILE
// ============================================

export async function fetchMyProfile() {
  const { data, error } = await supabase
    .from('profiles')
    .select('*, companies(*)')
    .single();

  if (error) throw error;
  return data;
}

export async function updateCompanyProfile(data: any) {
  const { error } = await supabase
    .from('companies')
    .update(data)
    .eq('profile_id', (await supabase.auth.getUser()).data.user?.id);

  if (error) throw error;
}

export async function uploadKYCDocument(file: File, profileId: string) {
  const allowedMimes = ['application/pdf', 'image/jpeg', 'image/png'];
  if (!allowedMimes.includes(file.type)) {
    throw new Error('Dateityp nicht erlaubt. Nur PDF, JPEG, PNG.');
  }
  if (file.size > 10 * 1024 * 1024) {
    throw new Error('Datei darf maximal 10 MB sein.');
  }

  const path = `${profileId}/${Date.now()}_${file.name}`;
  const { data, error } = await supabase.storage
    .from('kyc-documents')
    .upload(path, file);

  if (error) throw error;
  return data;
}
